import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { CrossDockPurchaseOrderTransformConfig } from 'components/logistics/components/crossDockPurchaseOrders/CrossDockPurchaseOrderFilterConfig';
import { CrossDockPurchaseOrderFilters } from 'components/logistics/components/crossDockPurchaseOrders/CrossDockPurchaseOrderFilters';
import { CrossDockPurchaseOrdersTable } from 'components/logistics/components/crossDockPurchaseOrders/CrossDockPurchaseOrdersTable';
import { useCrossDockPurchaseOrderActions } from 'components/logistics/components/crossDockPurchaseOrders/useCrossDockPurchaseOrderActions';
import { useCrossDockPurchaseOrdersSearch } from 'components/logistics/components/crossDockPurchaseOrders/useCrossDockPurchaseOrdersSearch';
import { getCrossDockPurchaseOrderViewPath } from 'components/logistics/components/crossDockPurchaseOrders/utils/crossDockPurchaseOrderUtils';
import {
    useCrossDockOptions,
    useFacilityOptions,
} from 'components/procurement/components/purchaseOrdersV2/queries/PurchaseOrderQueries';
import { Button } from 'shadcn/components/ui/Button';
import { fromUrl, pushToUrl, removeEmptyFilters, toApi, UnderConstruction } from 'shared';
import { ConfirmDialog } from 'shared/components/ConfirmDialog';
import { useIsFeatureOn } from 'shared/hooks/featureFlags/useIsFeatureOn';

const CROSS_DOCK_FEES_FEATURE_FLAG = 'CROSS_DOCK_FEES';
const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function CrossDockPurchaseOrders() {
    const history = useHistory();
    const location = useLocation();
    const { cancellingIds, cancelPurchaseOrder, downloadingIds, downloadPdf } = useCrossDockPurchaseOrderActions();
    const { data: crossDockPurchaseOrdersEnabled, isLoading: isFeatureLoading } =
        useIsFeatureOn(CROSS_DOCK_FEES_FEATURE_FLAG);

    const [initialState] = useState(() => {
        const parsed =
            typeof window !== 'undefined' ? fromUrl(location.search, CrossDockPurchaseOrderTransformConfig) : {};
        return {
            ...parsed,
            pageSize: parsed.pageSize ? Number(parsed.pageSize) : DEFAULT_PAGE_SIZE,
            offset: parsed.offset !== undefined ? Number(parsed.offset) : 0,
        };
    });

    const [filters, setFilters] = useState(initialState);
    const [purchaseOrderToCancel, setPurchaseOrderToCancel] = useState(null);

    const pageSize = filters.pageSize || DEFAULT_PAGE_SIZE;
    const offset = Number.isFinite(Number(filters.offset)) ? Number(filters.offset) : 0;
    const pageIndex = pageSize > 0 ? Math.floor(offset / pageSize) : 0;

    const apiFilters = useMemo(() => {
        const transformed = toApi(filters, CrossDockPurchaseOrderTransformConfig);
        return {
            ...transformed,
            limit: pageSize,
            offset,
        };
    }, [filters, offset, pageSize]);

    const listQuery = useCrossDockPurchaseOrdersSearch(apiFilters, {
        enabled: Boolean(crossDockPurchaseOrdersEnabled),
    });
    const { data: facilityOptions = [] } = useFacilityOptions({
        enabled: Boolean(crossDockPurchaseOrdersEnabled),
    });
    const { data: crossDockOptions = [] } = useCrossDockOptions({
        enabled: Boolean(crossDockPurchaseOrdersEnabled),
    });

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const parsed = fromUrl(location.search, CrossDockPurchaseOrderTransformConfig);
        setFilters({
            ...parsed,
            pageSize: parsed.pageSize ? Number(parsed.pageSize) : DEFAULT_PAGE_SIZE,
            offset: parsed.offset !== undefined ? Number(parsed.offset) : 0,
        });
    }, [location.search]);

    const facilityNameById = useMemo(
        () => new Map(facilityOptions.map((option) => [Number(option.value), option.label])),
        [facilityOptions],
    );
    const crossDockNameById = useMemo(
        () => new Map(crossDockOptions.map((option) => [Number(option.value), option.label])),
        [crossDockOptions],
    );

    const rows = listQuery.data?.rows ?? [];
    const pagination = listQuery.data?.pagination ?? {};
    const totalCount = pagination.totalCount ?? 0;
    const hasMore = Boolean(pagination.hasMore);
    const hasPrevious = pageIndex > 0;
    const totalPages = totalCount > 0 ? Math.ceil(totalCount / pageSize) : undefined;

    const filterValuesForUI = useMemo(() => {
        const { offset: _offset, pageSize: _pageSize, ...rest } = filters;
        return rest;
    }, [filters]);

    const handleFilterChange = useCallback(
        (nextFilters) => {
            const updatedState = {
                ...nextFilters,
                pageSize,
                offset: 0,
            };
            setFilters(updatedState);
            pushToUrl(removeEmptyFilters(updatedState), CrossDockPurchaseOrderTransformConfig, { replace: false });
        },
        [pageSize],
    );

    const handleNextPage = useCallback(() => {
        if (!hasMore) {
            return;
        }
        const nextState = { ...filters, offset: offset + pageSize };
        setFilters(nextState);
        pushToUrl(removeEmptyFilters(nextState), CrossDockPurchaseOrderTransformConfig, { replace: true });
    }, [filters, hasMore, offset, pageSize]);

    const handlePreviousPage = useCallback(() => {
        if (!hasPrevious) {
            return;
        }
        const nextState = { ...filters, offset: Math.max(0, offset - pageSize) };
        setFilters(nextState);
        pushToUrl(removeEmptyFilters(nextState), CrossDockPurchaseOrderTransformConfig, { replace: true });
    }, [filters, hasPrevious, offset, pageSize]);

    const handlePageSizeChange = useCallback(
        (newPageSize) => {
            const nextState = { ...filters, pageSize: newPageSize, offset: 0 };
            setFilters(nextState);
            pushToUrl(removeEmptyFilters(nextState), CrossDockPurchaseOrderTransformConfig, { replace: true });
        },
        [filters],
    );

    const handleCreate = useCallback(() => {
        history.push('/logistics/cross-dock-purchase-orders/create');
    }, [history]);

    const handleViewRow = useCallback(
        (row) => {
            if (row?.id) {
                history.push(getCrossDockPurchaseOrderViewPath(row.id));
            }
        },
        [history],
    );

    const handleConfirmCancel = useCallback(async () => {
        if (!purchaseOrderToCancel) {
            return;
        }
        const cancelled = await cancelPurchaseOrder(purchaseOrderToCancel);
        if (!cancelled) {
            return;
        }
        setPurchaseOrderToCancel(null);
    }, [cancelPurchaseOrder, purchaseOrderToCancel]);

    if (isFeatureLoading) {
        return null;
    }

    if (!crossDockPurchaseOrdersEnabled) {
        return <UnderConstruction title="Cross Dock Purchase Orders" />;
    }

    return (
        <div className="flex min-h-screen flex-col px-4 pb-6 pt-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h1 className="text-xl font-semibold">Cross Dock Purchase Orders</h1>
                <Button type="button" size="sm" onClick={handleCreate}>
                    Create Cross Dock Purchase Order
                </Button>
            </div>
            <div className="mt-4">
                <CrossDockPurchaseOrderFilters initialValues={filterValuesForUI} onChange={handleFilterChange} />
            </div>
            {listQuery.error?.message ? (
                <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                    {listQuery.error.message}
                </div>
            ) : null}
            <div className="mt-4">
                <CrossDockPurchaseOrdersTable
                    data={rows}
                    facilityNameById={facilityNameById}
                    crossDockNameById={crossDockNameById}
                    isFetching={listQuery.isFetching}
                    isLoading={listQuery.isLoading}
                    totalCount={totalCount}
                    hasNextPage={hasMore}
                    hasPrevious={hasPrevious}
                    onNext={handleNextPage}
                    onPrevious={handlePreviousPage}
                    currentPage={pageIndex + 1}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    onViewRow={handleViewRow}
                    onDownloadPdf={downloadPdf}
                    onCancelPurchaseOrder={setPurchaseOrderToCancel}
                    cancellingIds={cancellingIds}
                    downloadingIds={downloadingIds}
                />
            </div>
            <ConfirmDialog
                open={purchaseOrderToCancel != null}
                title="Cancel cross dock purchase order?"
                description="This action cannot be undone."
                confirmLabel={
                    purchaseOrderToCancel && cancellingIds.has(purchaseOrderToCancel.id)
                        ? 'Cancelling…'
                        : 'Yes, cancel PO'
                }
                confirmVariant="destructive"
                onCancel={() => setPurchaseOrderToCancel(null)}
                onConfirm={handleConfirmCancel}
            />
        </div>
    );
}

export { CrossDockPurchaseOrders };
